import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Send, Bot, User, Loader, Copy, Check } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';


// --- Custom Code Block Component ---
const CodeBlock = ({ language, value }) => {
  const [isCopied, setIsCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }).catch(err => {
      console.error('Failed to copy text: ', err);
    });
  };

  return (
    <div className="bg-gray-950 my-4 rounded-lg overflow-hidden border border-gray-700">
      <div className="flex justify-between items-center px-4 py-2 bg-gray-800 text-xs text-gray-400">
        <span>{language || 'code'}</span>
        <button onClick={handleCopy} className="flex items-center gap-1">
          {isCopied ? <Check size={16} className="text-green-400" /> : <Copy size={16} />}
          {isCopied ? 'Copied!' : 'Copy code'}
        </button>
      </div>
      <SyntaxHighlighter
        language={language}
        style={vscDarkPlus}
        customStyle={{ margin: 0, padding: '1rem', background: 'transparent' }}
        codeTagProps={{ style: { fontFamily: '"Fira Code", monospace' } }}
      >
        {value}
      </SyntaxHighlighter>
    </div>
  );
};


// --- Main App Component ---
export default function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  useEffect(() => {
    setMessages([
      {
        text: "Hello! I'm your integrated AI assistant for OneCRM. Ask me to write some code! For example: `Write a javascript function to find the factorial of a number`",
        isUser: false,
      },
    ]);
  }, []);

  const handleSend = async () => {
    if (input.trim() === '' || isLoading) return;

    const userMessage = { text: input, isUser: true };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/chat`, {
        message: input,
      });

      const aiMessage = { text: response.data.response, isUser: false };
      setMessages(prev => [...prev, aiMessage]);

    } catch (error) {
      console.error("Error fetching AI response:", error);
      const errorMessage = { text: "Sorry, I'm having trouble connecting. Please try again later.", isUser: false };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  // Custom renderers for ReactMarkdown
  const markdownComponents = {
    code({ node, inline, className, children, ...props }) {
      const match = /language-(\w+)/.exec(className || '');
      const codeValue = String(children).replace(/\n$/, '');
      if (!inline && match) {
        return <CodeBlock language={match[1]} value={codeValue} {...props} />;
      }
      return inline ? (
        <code className="bg-gray-700 text-sm rounded px-1 py-0.5">{children}</code>
      ) : (
        <CodeBlock language={null} value={codeValue} {...props} />
      );
    },
  };

  return (
    <div className="flex h-screen bg-gray-900 text-white font-sans">


      {/* Main Chat Area */}
      <div className="flex-1 flex flex-col">
        <h1 className="text-2xl font-bold mb-6 text-gray-200 text-center">OneBot AI</h1>

        <main className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="max-w-4xl mx-auto">
            {messages.map((msg, index) => (
              <div key={index} className="flex items-start gap-4 my-6">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${msg.isUser ? 'bg-gray-700' : 'bg-indigo-500'}`}>
                  {msg.isUser ? <User size={20} /> : <Bot size={20} />}
                </div>
                <div className="p-4 rounded-2xl max-w-2xl prose prose-invert prose-p:my-2 prose-headings:my-4 prose-blockquote:my-2 prose-li:my-0 bg-gray-800">
                   <ReactMarkdown components={markdownComponents}>
                    {msg.text}
                  </ReactMarkdown>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="flex items-start gap-4 my-6">
                <div className="w-8 h-8 bg-indigo-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Loader className="animate-spin" size={20} />
                </div>
                <div className="p-4 rounded-2xl bg-gray-800">
                  <p>Thinking...</p>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </main>

        {/* Input Area */}
        <footer className="p-4 md:p-6 bg-gray-900/50 backdrop-blur-sm border-t border-gray-800">
          <div className="max-w-4xl mx-auto">
            <div className="relative">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Message your AI assistant..."
                className="w-full bg-gray-800 border border-gray-700 rounded-2xl p-4 pr-20 resize-none focus:outline-none focus:ring-2 focus:ring-indigo-500"
                rows="1"
                disabled={isLoading}
              />
              <button
                onClick={handleSend}
                disabled={isLoading || input.trim() === ''}
                className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-indigo-600 hover:bg-indigo-500 disabled:bg-gray-600 disabled:cursor-not-allowed transition-colors"
              >
                <Send size={20} />
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}